'use client';
import React, { useState } from 'react';
import { useOptionChain } from '@benzinga/hooks';

import type { BaseQuotePageProps } from '../../../../../src/utils/quote/types';
import { useQuoteTrackClick } from '../../../../../src/utils/quote/useQuoteTracking';
import OptionChainTable from '../../../../../src/components/Quote/optionChain/OptionChainTable';
import OptionChainFilter from '../../../../../src/components/Quote/optionChain/OptionChainFilter';
import OptionsSummaryStats from '../../../../../src/components/Quote/optionChain/OptionsSummaryStats';
import OptionTabs from '../../../../../src/components/Quote/optionChain/OptionTabs';
import {
  VolumeVsOIByStrikeChart,
  VolumeByExpirationChart,
} from '../../../../../src/components/Quote/optionChain/OptionsCharts';
import type { OrganizeOptionDataReturn, OptionChainFilterProps } from '@benzinga/data-option-chain';
import { hasValidOptionExpiry } from '../../../../../src/utils/quote/optionChainValidity';

const OptionChainSection: React.FC<{
  optionChainData: OrganizeOptionDataReturn;
  stockPrice: number;
  symbol: string;
}> = ({ optionChainData, stockPrice, symbol }) => {
  const [selectedExpiryKey, setSelectedExpiryKey] = useState<string>('');
  const trackClick = useQuoteTrackClick();
  const {
    expiries,
    filteredData,
    initialRange,
    moneyness,
    onExpiryChange,
    onMoneynessChange,
    onRangeChange,
    range,
  } = useOptionChain({ optionChainData, stockPrice, symbol, step: 5 });

  const activeExpiryKey = selectedExpiryKey || expiries[0]?.value || '';
  if (expiries.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-gray-400">
        Options data is not available for this ticker.
      </p>
    );
  }

  const handleExpiryChange = (value: string | number) => {
    setSelectedExpiryKey(value as string);
    onExpiryChange(value);
    trackClick('option_chain_expiry');
  };

  const handleMoneynessChange: OptionChainFilterProps['onMoneynessChange'] = value => {
    onMoneynessChange(value);
    trackClick('option_chain_moneyness');
  };

  return (
    <>
      <OptionsSummaryStats optionChainData={optionChainData} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <VolumeVsOIByStrikeChart expiryKey={activeExpiryKey} optionChainData={optionChainData} />
        <VolumeByExpirationChart optionChainData={optionChainData} />
      </div>

      <OptionChainFilter
        expiries={expiries}
        moneyNess={moneyness}
        onExpiryChange={handleExpiryChange}
        onMoneynessChange={handleMoneynessChange}
        onRangeChange={onRangeChange}
        range={range}
        rangeLimits={initialRange}
      />

      <OptionChainTable data={filteredData} />
    </>
  );
};

interface OptionChainContentProps extends BaseQuotePageProps {
  optionChainData: OrganizeOptionDataReturn | null;
}

const OptionChainContent: React.FC<OptionChainContentProps> = ({
  metaProps,
  optionChainData,
  profile,
  symbol,
}) => {
  const stockPrice = Number(profile?.richQuoteData?.lastTradePrice ?? 0);
  const hasData = hasValidOptionExpiry(optionChainData, symbol) && stockPrice > 0;
  return (
    <>
      <OptionTabs activeTab="option-chain" symbol={symbol} />
      <div className="main-div flex flex-col gap-y-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-gray-800">{metaProps.title}</h1>
          <p className="text-sm text-gray-500">{metaProps.description}</p>
        </div>

        {hasData ? (
          <OptionChainSection
            optionChainData={optionChainData}
            stockPrice={stockPrice}
            symbol={symbol}
          />
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">
            Options data is not available for this ticker.
          </p>
        )}

      </div>
    </>
  );
};

export default OptionChainContent;
